/**
 * PCR Card Responsive JavaScript
 * Handles all mobile interactions and responsive behaviors
 */

class ResponsiveHandler {
    constructor() {
        this.mobileBreakpoint = 768;
        this.isMobile = window.innerWidth < this.mobileBreakpoint;
        this.mobileMenuOpen = false;
        this.touchStartX = 0;
        this.touchEndX = 0;
        
        this.init();
    }

    init() {
        this.setupMobileMenu();
        this.setupFormEnhancements();
        this.setupTouchHandlers();
        this.setupResizeHandler();
        this.setupScrollEffects();
        this.fixIOSInputZoom();
        this.setupSwipeGestures();
        this.setupLazyLoading();
    }

    /**
     * Mobile Menu Handler
     */
    setupMobileMenu() {
        const menuToggle = document.querySelector('.mobile-menu-toggle');
        const nav = document.querySelector('.modern-nav');
        const body = document.body;

        if (!menuToggle || !nav) return;

        menuToggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.mobileMenuOpen = !this.mobileMenuOpen;
            
            menuToggle.classList.toggle('active');
            nav.classList.toggle('mobile-active');
            body.classList.toggle('menu-open');
            
            // Prevent body scroll when menu is open
            if (this.mobileMenuOpen) {
                body.style.overflow = 'hidden';
                this.createOverlay();
            } else {
                body.style.overflow = '';
                this.removeOverlay();
            }
        });

        // Close menu when clicking nav links
        nav.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', () => {
                if (this.isMobile && this.mobileMenuOpen) {
                    this.closeMobileMenu();
                }
            });
        });
    }

    createOverlay() {
        const overlay = document.createElement('div');
        overlay.className = 'mobile-menu-overlay';
        overlay.addEventListener('click', () => this.closeMobileMenu());
        document.body.appendChild(overlay);
        
        // Fade in
        setTimeout(() => overlay.classList.add('active'), 10);
    }

    removeOverlay() {
        const overlay = document.querySelector('.mobile-menu-overlay');
        if (overlay) {
            overlay.classList.remove('active');
            setTimeout(() => overlay.remove(), 300);
        }
    }

    closeMobileMenu() {
        const menuToggle = document.querySelector('.mobile-menu-toggle');
        const nav = document.querySelector('.modern-nav');
        
        this.mobileMenuOpen = false;
        menuToggle?.classList.remove('active');
        nav?.classList.remove('mobile-active');
        document.body.classList.remove('menu-open');
        document.body.style.overflow = '';
        this.removeOverlay();
    }

    /**
     * Form Enhancements for Mobile
     */
    setupFormEnhancements() {
        // Floating labels
        document.querySelectorAll('.form-control-modern').forEach(input => {
            // Add placeholder for mobile
            if (this.isMobile && !input.placeholder) {
                const label = input.parentElement.querySelector('.form-label-floating');
                if (label) {
                    input.placeholder = label.textContent;
                }
            }

            // Handle focus/blur for floating labels
            input.addEventListener('focus', () => {
                input.parentElement.classList.add('focused');
            });

            input.addEventListener('blur', () => {
                if (!input.value) {
                    input.parentElement.classList.remove('focused');
                }
            });

            // Initial state
            if (input.value) {
                input.parentElement.classList.add('has-value');
            }
        });

        // Auto-resize textareas
        document.querySelectorAll('textarea').forEach(textarea => {
            const resize = () => {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
            };
            
            textarea.addEventListener('input', resize);
            resize(); // Initial resize
        });
    }

    /**
     * Touch Handlers for Better Mobile Experience
     */
    setupTouchHandlers() {
        // Add touch feedback to buttons
        document.querySelectorAll('button, .btn, .card, a').forEach(element => {
            element.addEventListener('touchstart', () => {
                element.classList.add('touch-active');
            });

            element.addEventListener('touchend', () => {
                setTimeout(() => element.classList.remove('touch-active'), 150);
            });
        });

        // Prevent double-tap zoom on buttons
        let lastTouchEnd = 0;
        document.addEventListener('touchend', (e) => {
            const now = Date.now();
            if (now - lastTouchEnd <= 300) {
                e.preventDefault();
            }
            lastTouchEnd = now;
        }, { passive: false });
    }

    /**
     * Handle Window Resize
     */
    setupResizeHandler() {
        let resizeTimer;
        
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const wasM = this.isMobile;
                this.isMobile = window.innerWidth < this.mobileBreakpoint;
                
                // Close mobile menu if resizing to desktop
                if (wasM && !this.isMobile && this.mobileMenuOpen) {
                    this.closeMobileMenu();
                }
                
                // Update viewport height for mobile browsers
                this.updateViewportHeight();
            }, 250);
        });
    }

    /**
     * Update viewport height (fixes mobile browser chrome issues)
     */
    updateViewportHeight() {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
    }

    /**
     * Scroll Effects
     */
    setupScrollEffects() {
        let lastScroll = 0;
        const header = document.querySelector('.modern-header');
        
        if (!header) return;

        window.addEventListener('scroll', () => {
            const currentScroll = window.pageYOffset;
            
            // Hide/show header on scroll
            if (this.isMobile) {
                if (currentScroll > lastScroll && currentScroll > 100) {
                    header.classList.add('header-hidden');
                } else {
                    header.classList.remove('header-hidden');
                }
            }
            
            // Add shadow on scroll
            if (currentScroll > 0) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }
            
            lastScroll = currentScroll;
        });

        // Scroll to top button
        const scrollTopBtn = document.querySelector('.scroll-to-top');
        if (scrollTopBtn) {
            window.addEventListener('scroll', () => {
                if (window.pageYOffset > 300) {
                    scrollTopBtn.classList.add('visible');
                } else {
                    scrollTopBtn.classList.remove('visible');
                }
            });

            scrollTopBtn.addEventListener('click', () => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    /**
     * Fix iOS Input Zoom
     */
    fixIOSInputZoom() {
        if (!this.isIOS()) return;

        document.querySelectorAll('input[type="text"], input[type="email"], input[type="password"], textarea, select').forEach(input => {
            input.style.fontSize = '16px';
        });

        // Prevent zoom on double tap
        document.addEventListener('gesturestart', (e) => {
            e.preventDefault();
        });
    }

    /**
     * Swipe Gesture Support
     */
    setupSwipeGestures() {
        document.addEventListener('touchstart', (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        document.addEventListener('touchend', (e) => {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleSwipeGesture();
        }, { passive: true });
    }

    handleSwipeGesture() {
        const swipeThreshold = 50;
        const diff = this.touchEndX - this.touchStartX;

        // Swipe right to open menu
        if (diff > swipeThreshold && !this.mobileMenuOpen && this.touchStartX < 20) {
            const menuToggle = document.querySelector('.mobile-menu-toggle');
            menuToggle?.click();
        }

        // Swipe left to close menu
        if (diff < -swipeThreshold && this.mobileMenuOpen) {
            this.closeMobileMenu();
        }
    }

    /**
     * Lazy Loading for Images
     */
    setupLazyLoading() {
        const images = document.querySelectorAll('img[data-src]');
        
        if ('IntersectionObserver' in window) {
            const imageObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        img.src = img.dataset.src;
                        img.classList.add('loaded');
                        imageObserver.unobserve(img);
                    }
                });
            });

            images.forEach(img => imageObserver.observe(img));
        } else {
            // Fallback for older browsers
            images.forEach(img => {
                img.src = img.dataset.src;
                img.classList.add('loaded');
            });
        }
    }

    /**
     * Utility Functions
     */
    isIOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }

    isAndroid() {
        return /Android/.test(navigator.userAgent);
    }

    isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    /**
     * Responsive Table Handler
     */
    static initResponsiveTables() {
        document.querySelectorAll('.table-responsive').forEach(table => {
            const wrapper = document.createElement('div');
            wrapper.className = 'table-responsive-wrapper';
            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);

            // Add data labels for mobile view
            const headers = Array.from(table.querySelectorAll('thead th')).map(th => th.textContent);
            
            table.querySelectorAll('tbody tr').forEach(row => {
                row.querySelectorAll('td').forEach((td, index) => {
                    td.setAttribute('data-label', headers[index] || '');
                });
            });
        });
    }

    /**
     * Modal Enhancement for Mobile
     */
    static enhanceModals() {
        document.querySelectorAll('.modal').forEach(modal => {
            modal.addEventListener('show.bs.modal', () => {
                if (window.innerWidth < 768) {
                    modal.classList.add('modal-fullscreen-mobile');
                }
            });
        });
    }

    /**
     * Form Validation Enhancement
     */
    static enhanceValidation() {
        document.querySelectorAll('.needs-validation').forEach(form => {
            form.addEventListener('submit', (e) => {
                if (!form.checkValidity()) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Scroll to first error
                    const firstError = form.querySelector('.is-invalid');
                    if (firstError) {
                        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        firstError.focus();
                    }
                }
                
                form.classList.add('was-validated');
            });
        });
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    // Initialize responsive handler
    window.responsiveHandler = new ResponsiveHandler();
    
    // Initialize static methods
    ResponsiveHandler.initResponsiveTables();
    ResponsiveHandler.enhanceModals();
    ResponsiveHandler.enhanceValidation();
    
    // Update viewport height
    window.responsiveHandler.updateViewportHeight();
});

// Export for use in other modules
export default ResponsiveHandler;